import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Check, Sparkle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const plans = [
  {
    id: "free",
    name: "Free",
    features: [
      "AI Career Roadmap + Skill Gap Analysis",
      "5 AI Coach messages/month",
      "1 Resume Review/month (limited)",
      "2 Job Matches",
      "Free Learning + Certifications",
      "Profile, XP, Streaks & Badges",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    features: [
      "7-day Pro trial for eligible new accounts, then monthly billing",
      "100 AI Coach messages/month",
      "3 full Resume Reviews/month",
      "3 Interview Sessions/month",
      "6 Job Matches + weekly refresh",
      "Salary Tool · 50 searches/month",
      "Resume Builder · 5 resumes/month",
      "All Resume AI + ATS + version history",
      "PDF export",
    ],
    popular: true,
  },
  {
    id: "premium",
    name: "Premium",
    features: [
      "200 AI Coach messages/month",
      "5 full Resume Reviews/month",
      "5 Interview Sessions/month",
      "6 Job Matches + weekly refresh",
      "Salary Tool · 50 searches/month",
      "Resume Builder · 10 resumes/month",
      "All Resume AI + ATS + version history",
      "PDF + DOCX export",
    ],
  },
];

const pricing = {
  IN: {
    currency: "INR",
    symbol: "₹",
    pro: 149,
    premium: 249,
  },
  AE: {
    currency: "AED",
    symbol: "AED ",
    pro: 9.99,
    premium: 19.99,
  },
  USD: {
    currency: "USD",
    symbol: "$",
    pro: 4.99,
    premium: 7.99,
  },
};

function getRegion(country) {
  const value = (country || "").trim().toLowerCase();

  if (value === "india" || value === "in") {
    return "IN";
  }

  if (
    value === "uae" ||
    value === "united arab emirates" ||
    value === "uae - united arab emirates" ||
    value === "ae"
  ) {
    return "AE";
  }

  // USA + every other country uses USD
  return "USD";
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Pricing() {
  const { user } = useAuth();
  const [buying, setBuying] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const country =
    user?.profile?.country ||
    user?.country ||
    "";

  const region = getRegion(country);
  const regionPricing = pricing[region];

  useEffect(() => {
    loadRazorpayScript();
  }, []);

  const getDisplayPrice = (planId) => {
    if (planId === "free") {
      return {
        amount: 0,
        symbol: "",
        currency: "",
      };
    }

    const amount = regionPricing[planId];

    return {
      amount,
      symbol: regionPricing.symbol,
      currency: regionPricing.currency,
    };
  };

  const upgrade = async (planId) => {
    if (planId === "free") return;

    setBuying(planId);

    try {
      const loaded = await loadRazorpayScript();

      if (!loaded) {
        toast.error("Unable to load Razorpay");
        setBuying(null);
        return;
      }

      const { data } = await api.post("/payments/checkout", {
        package_id: planId,
        origin_url: window.location.origin,
      });

      const isProTrial = Boolean(data.is_pro_trial);

      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,

        name: "Career GPS AI",

        description:
          planId === "premium"
            ? "Career GPS AI Premium Subscription"
            : isProTrial
            ? "Career GPS AI Pro — 7-Day Free Trial"
            : "Career GPS AI Pro Subscription",

        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },

        theme: {
          color: "#2563eb",
        },

        handler: async function (response) {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_payment_id:
                response.razorpay_payment_id,

              razorpay_subscription_id:
                response.razorpay_subscription_id,

              razorpay_signature:
                response.razorpay_signature,
            });

            window.location.assign(
              `/payment-success?subscription_id=${encodeURIComponent(
                response.razorpay_subscription_id
              )}`
            );
          } catch (err) {
            console.error(
              "Razorpay verification failed",
              err
            );

            toast.error(
              "Payment completed but verification failed. Please contact support."
            );

            setBuying(null);
          }
        },

        modal: {
          ondismiss: function () {
            setBuying(null);
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        console.error(
          "Razorpay payment failed",
          response.error
        );

        toast.error(
          response.error?.description ||
            "Payment failed"
        );

        setBuying(null);
      });

      razorpay.open();
    } catch (err) {
      console.error("Razorpay checkout failed", err);

      toast.error(
        err.response?.data?.detail ||
          "Unable to create Razorpay subscription"
      );

      setBuying(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505]"
      data-testid="pricing-page"
    >
      <Navbar />
      <DashSidebar />

      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-6xl mx-auto lg:mx-0">

        <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">
          // Upgrade
        </div>

        <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">
          Choose your plan
        </h1>

        <p className="text-zinc-500 mt-2 text-sm sm:text-base">
          Current plan:{" "}
          <span className="text-blue-400 capitalize">
            {user?.tier || "free"}
          </span>
          {user?.is_trial && user?.trial_ends_at && (
            <span className="text-zinc-400">
              {" "}· Pro trial ends {new Date(user.trial_ends_at).toLocaleDateString()}
            </span>
          )}
        </p>

        {user?.tier === "free" && !user?.pro_trial_used && (
          <div className="mt-6 rounded-xl border border-blue-500/30 bg-blue-500/10 px-5 py-4">
            <div className="text-sm font-medium text-white">Try Pro free for 7 days</div>
            <div className="text-xs text-zinc-400 mt-1">
              You authorize recurring billing now. No Pro subscription charge is made during the 7-day trial; if you do not cancel, Razorpay automatically starts the monthly Pro billing after the trial.
            </div>
          </div>
        )}

        <div className="mt-8 sm:mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">

          {plans.map((p) => {
            const current = user?.tier === p.id;
            const display = getDisplayPrice(p.id);

            return (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative bg-[#0F0F12] border rounded-xl p-6 sm:p-8 ${
                  p.popular
                    ? "border-blue-500/50 glow-blue sm:col-span-2 md:col-span-1"
                    : "border-white/10"
                }`}
              >

                {p.popular && (
                  <div className="absolute -top-3 left-8 bg-blue-600 text-white text-[10px] tracking-[0.2em] uppercase font-semibold px-3 py-1 rounded">
                    Most popular
                  </div>
                )}

                <h3 className="text-2xl font-medium">
                  {p.name}
                </h3>

                <div className="mt-4 flex items-baseline gap-1">

                  {p.id === "free" ? (
                    <span className="text-5xl font-medium tracking-tight">
                      Free
                    </span>
                  ) : (
                    <>
                      <span className="text-5xl font-medium tracking-tight">
                        {display.symbol}
                        {display.amount}
                      </span>

                      <span className="text-zinc-500 text-sm">
                        /mo
                      </span>
                    </>
                  )}

                </div>

                {p.id !== "free" && (
                  <div className="text-zinc-500 text-xs mt-2">
                    {display.currency}
                  </div>
                )}

                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-zinc-300"
                    >
                      <Check
                        size={16}
                        className="text-blue-400 mt-0.5 shrink-0"
                      />

                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => upgrade(p.id)}
                  disabled={
                    current ||
                    buying === p.id
                  }
                  data-testid={`plan-cta-${p.id}`}
                  className={`mt-8 w-full font-medium py-3 rounded-lg transition-colors ${
                    current
                      ? "bg-white/5 border border-white/10 text-zinc-500 cursor-default"
                      : p.popular
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-white/5 hover:bg-white/10 border border-white/10 text-white"
                  }`}
                >

                  {current ? (
                    "Current plan"
                  ) : buying === p.id ? (
                    "Loading…"
                  ) : p.id === "free" ? (
                    "Free forever"
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Sparkle
                        size={14}
                        weight="fill"
                      />
                      {p.id === "pro" && user?.tier === "free" && !user?.pro_trial_used
                        ? "Start 7-Day Pro Trial"
                        : "Upgrade"}
                    </span>
                  )}

                </button>

              </div>
            );
          })}

        </div>

        {user?.is_trial && user?.razorpay_subscription_id && (
          <div className="mt-8 rounded-xl border border-white/10 bg-[#0F0F12] p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-white">7-day Pro trial active</div>
              <div className="text-xs text-zinc-500 mt-1">Cancel before the trial ends and the Pro subscription will be cancelled before the first recurring charge.</div>
            </div>
            <button
              disabled={cancelling}
              onClick={async () => {
                if (!window.confirm("Cancel your Pro trial? Your account will return to Free and the recurring Pro charge will be cancelled.")) return;
                setCancelling(true);
                try {
                  await api.post("/payments/razorpay/cancel");
                  toast.success("Pro trial cancelled. You are back on Free.");
                  window.location.reload();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Unable to cancel subscription");
                  setCancelling(false);
                }
              }}
              className="border border-red-500/30 text-red-300 hover:bg-red-500/10 px-4 py-2 rounded-lg text-sm"
            >
              {cancelling ? "Cancelling…" : "Cancel trial"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
